import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import packageJson from '../package.json';

global.appVersion = packageJson.version;

// Minimum interval between version checks (5 minutes)
const CHECK_COOLDOWN_MS = 5 * 60 * 1000;

// Compare two semantic versions
const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split('.').map(Number);
  const versionsB = versionB.split('.').map(Number);
  const maxLen = Math.max(versionsA.length, versionsB.length);

  for (let i = 0; i < maxLen; i++) {
    const a = versionsA[i] || 0;
    const b = versionsB[i] || 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return false;
};

const CacheBuster = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isLatestVersion, setIsLatestVersion] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingVersion, setPendingVersion] = useState(null);

  const history = useHistory();

  // Guards
  const isReloadingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const hasDetectedRef = useRef(false);

  const refreshCacheAndReload = useCallback(async (version) => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;

    setIsUpdating(true);

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Service worker caches cleared');
      }
    } catch (error) {
      console.warn('Cache clear failed', error);
    }

    // Store the version to prevent reload loop after refresh
    localStorage.setItem('acknowledgedVersion', version);

    // Brief delay so user sees the updating state
    setTimeout(() => {
      window.location.href = `${window.location.origin}${window.location.pathname}?reload=${Date.now()}`;
    }, 1000);
  }, []);

  // Core version check function — reused by all triggers
  const checkVersion = useCallback(async () => {
    if (isReloadingRef.current || hasDetectedRef.current) return;

    // Cooldown: skip if checked recently
    const now = Date.now();
    if (now - lastCheckTimeRef.current < CHECK_COOLDOWN_MS) {
      return;
    }
    lastCheckTimeRef.current = now;

    try {
      const res = await fetch(`/meta.json?t=${now}`, {
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      const meta = await res.json();
      const latestVersion = meta.version;
      const currentVersion = global.appVersion;
      const acknowledgedVersion = localStorage.getItem('acknowledgedVersion');

      // Skip if already on this version (post-reload)
      if (latestVersion === acknowledgedVersion) {
        console.log('App version already acknowledged.');
        return;
      }

      const isNewVersion = semverGreaterThan(latestVersion, currentVersion);

      if (isNewVersion) {
        console.log(`New version found: ${latestVersion} (current: ${currentVersion})`);
        hasDetectedRef.current = true;
        setPendingVersion(latestVersion);
        setIsLatestVersion(false);
      } else {
        console.log('App is up-to-date.');
      }
    } catch (error) {
      console.error('Could not fetch version metadata', error);
    }
  }, []);

  // ─── Trigger 1: On initial mount ───
  useEffect(() => {
    lastCheckTimeRef.current = 0;
    checkVersion().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Trigger 2: On route change (with cooldown) ───
  useEffect(() => {
    if (!history) return;

    const unlisten = history.listen(() => {
      checkVersion();
    });

    return () => unlisten();
  }, [history, checkVersion]);

  // ─── Trigger 3: On tab re-focus (visibility change) ───
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkVersion]);

  // Handler for the "Update Now" button
  const handleUpdateClick = () => {
    if (pendingVersion) {
      refreshCacheAndReload(pendingVersion);
    }
  };

  // ─── Full-screen overlay: "Updating..." (after user clicks Update Now) ───
  if (isUpdating) {
    return (
      <div style={styles.overlay}>
        <div style={styles.spinner} />
        <h2 style={styles.title}>Updating to latest version...</h2>
        <p style={styles.subtitle}>Please wait, the page will reload automatically.</p>
        <style>{spinnerKeyframes}</style>
      </div>
    );
  }

  // ─── Full-screen overlay: "Update Available" (waiting for user to click) ───
  if (pendingVersion && !isLatestVersion && !loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.iconWrapper}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#956ee7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <h2 style={styles.cardTitle}>Update Available</h2>
          <p style={styles.cardText}>
            A new version <strong style={{ color: '#956ee7' }}>v{pendingVersion}</strong> is available.
            <br />
            Please update to continue using the app.
          </p>
          <button
            onClick={handleUpdateClick}
            style={styles.updateButton}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#7c5bc9'; e.target.style.transform = 'scale(1.02)'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#956ee7'; e.target.style.transform = 'scale(1)'; }}
          >
            Update Now
          </button>
          <p style={styles.currentVersion}>Current version: v{global.appVersion}</p>
        </div>
        <style>{spinnerKeyframes}</style>
      </div>
    );
  }

  if (loading) return null;

  return children({ loading, isLatestVersion, refreshCacheAndReload });
};

// ─── Styles ───
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 99999,
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(255, 255, 255, 0.2)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'cachebusters-spin 0.8s linear infinite',
  },
  title: {
    marginTop: '24px',
    fontSize: '20px',
    fontWeight: 600,
  },
  subtitle: {
    marginTop: '8px',
    fontSize: '14px',
    opacity: 0.7,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '40px 48px',
    textAlign: 'center',
    maxWidth: '420px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  iconWrapper: {
    marginBottom: '16px',
  },
  cardTitle: {
    color: '#1a1a1a',
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 12px 0',
  },
  cardText: {
    color: '#555',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: '0 0 24px 0',
  },
  updateButton: {
    backgroundColor: '#956ee7',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 36px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
  },
  currentVersion: {
    color: '#999',
    fontSize: '12px',
    marginTop: '16px',
    marginBottom: 0,
  },
};

const spinnerKeyframes = `@keyframes cachebusters-spin { to { transform: rotate(360deg); } }`;

export default CacheBuster;
